import React, { useState, useMemo } from 'react';
import { Book, Search, ExternalLink, Mail, Filter, BookOpen, Users, Calendar } from 'lucide-react';

const ReferencePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const references = [
    {
      id: 1,
      category: 'Substance Abuse',
      title: 'Personal Drinking Questionnaire (SOCRATES 8A)',
      authors: 'Agree, S.',
      journal: 'Psychology of Addictive Behaviors',
      year: '2020',
      url: 'casaa.unm.edu/inst/SOCRATESv8.pdf',
      citation: 'Agree, S. "Personal Drinking Questionnaire (SOCRATES 8A." Siue Edu, Psychology of Addictive Behaviors, casaa.unm.edu/inst/SOCRATESv8.pdf. Accessed 24 Oct. 2020.'
    },
    {
      id: 2,
      category: 'Psychiatric Assessment',
      title: 'Brief Psychiatric Rating Scale',
      authors: 'Andersen, J., Larsen, J. K., Schultz, V., Nielsen, B. M., Kørner, A., Behnke, K., et al.',
      journal: 'Psychopathology, Karger AG',
      year: '1985',
      url: 'www.karger.com/Article/Abstract/284591',
      citation: 'Andersen, J., Larsen, J. K., Schultz, V., Nielsen, B. M., Kørner, A., Behnke, K., ... & Bech. "Brief Psychiatric Rating Scale." Psychopathology, Karger AG, 1985, www.karger.com/Article/Abstract/284591'
    },
    {
      id: 3,
      category: 'Substance Abuse',
      title: 'The Alcohol Use Disorders Identification Test',
      authors: 'Babor, Thomas F., et al.',
      journal: 'WHO Publication',
      year: '2001',
      citation: 'Babor, Thomas F., et al. "The alcohol use disorders identification test." (2001): 2017.'
    },
    {
      id: 4,
      category: 'Autism Spectrum',
      title: 'Autism Spectrum Quotient',
      authors: 'Baron-Cohen, S., Wheelwright, S., Skinner, R., Martin, J., & Clubley, E.',
      journal: 'Journal of Autism and Developmental Disorders',
      year: '2001',
      url: 'thinkcbt.com/autism-spectrum-quotient-aq-questionnaire',
      citation: 'Baron-Cohen, S., Wheelwright, S., Skinner, R., Martin, J., & Clubley, E. "Autism Spectrum Quotient." Think CBT, Journal of Autism and Developmental Disorders volume, Feb. 2001, thinkcbt.com/autism-spectrum-quotient-aq-questionnaire.'
    },
    {
      id: 5,
      category: 'Risk Assessment',
      title: 'Suicide Risk: Structured Professional Judgement',
      authors: 'Bouch, Joe, and John James Marshall',
      journal: 'Advances in Psychiatric Treatment',
      year: '2005',
      citation: 'Bouch, Joe, and John James Marshall. "Suicide risk: structured professional judgement." Advances in Psychiatric Treatment 11.2 (2005): 84-91.'
    },
    {
      id: 6,
      category: 'Depression',
      title: 'Major Depression Inventory (MDI)',
      authors: 'Bech, P. E. R., & Wermuth, L.',
      journal: 'Nordic Journal of Psychiatry',
      year: '1998',
      url: 'qxmd.com/calculate/calculator_542/major-depression-inventory-mdi',
      citation: 'Bech, P. E. R., & Wermuth, L. "Major Depression Inventory (MDI)." Taylor & Francis Online, Nordic Journal of Psychiatry, 1998, qxmd.com/calculate/calculator_542/major-depression-inventory-mdi.'
    },
    {
      id: 7,
      category: 'Anxiety',
      title: 'GAD7 (Anxiety)',
      authors: 'Bernd Löwe, Oliver Decker, Stefanie Müller, Elmar Brähler, et al.',
      journal: 'Lippincott Williams & Wilkins',
      year: '2008',
      url: 'www.jstor.org/stable/40221654',
      citation: 'Bernd Löwe, Oliver Decker, Stefanie Müller, Elmar Brähler, Dieter Schellberg, Wolfgang Herzog and Philipp Yorck Herzberg. "GAD7 (Anxiety)." Jstor, Lippincott Williams & Wilkins, 3 Mar. 2008, www.jstor.org/stable/40221654.'
    },
    {
      id: 8,
      category: 'Relationships',
      title: 'Couples Conflict Questionnaire',
      authors: 'Buss, D. M.',
      journal: 'Journal of Personality',
      year: '1991',
      url: 'thinkcbt.com/couples-conflict-relationships',
      citation: 'Buss, D. M. (1991). "Couples Conflict Questionnaire." Think CBT, Journal of Personality, 1991, thinkcbt.com/couples-conflict-relationships.'
    },
    {
      id: 9,
      category: 'Sleep',
      title: 'The Insomnia Severity Index',
      authors: 'Célyne H Bastien, Annie Vallières, Charles M Morin',
      journal: 'Sleep Medicine',
      year: '2001',
      url: 'thinkcbt.com/insomnia-severity-index',
      citation: 'Célyne HBastienAnnieVallièresCharles MMorin. "The Insomnia Severity Index." ScienceDirect, Sleep Medicine, July 2001, thinkcbt.com/insomnia-severity-index.'
    },
    {
      id: 10,
      category: 'Pain Assessment',
      title: 'Pain Inventory',
      authors: 'Cleeland, C. S., & Ryan, K. M.',
      journal: 'American Psychological Association',
      year: '1994',
      url: 'psycnet.apa.org/record/2015-49222-001',
      citation: 'Cleeland, C. S., & Ryan, K. M. (1994). "Pain Inventory." APA PsycNet, American Psychological Association, 1994, psycnet.apa.org/record/2015-49222-001.'
    }
  ];

  // Add more references (continuing with the pattern from the provided list)
  const additionalReferences = [
    {
      id: 11,
      category: 'PTSD',
      title: 'Impact of Event Scale - Revised (IES-R) for DSM-IV',
      authors: 'Christianson, S., & Marren, J.',
      journal: 'National Center for PTSD',
      year: '2012',
      url: 'www.ptsd.va.gov/professional/assessment/adult-sr/ies',
      citation: 'Christianson, S., & Marren, J. "Impact of Event Scale - Revised (IES-R) for DSM-IV." National Center for PTSD., Medsurg Nursing, 2012, www.ptsd.va.gov/professional/assessment/adult-sr/ies.'
    },
    {
      id: 12,
      category: 'Social Anxiety',
      title: 'Social Phobia Inventory (SPIN)',
      authors: 'Connor, Kathryn M., et al.',
      journal: 'The British Journal of Psychiatry',
      year: '2000',
      citation: 'Connor, Kathryn M., et al. "Psychometric properties of the Social Phobia Inventory (SPIN): New self-rating scale." The British Journal of Psychiatry 176.4 (2000): 379-386.'
    },
    {
      id: 13,
      category: 'Fatigue',
      title: 'Chalder Fatigue Scale',
      authors: 'Craig Jackson',
      journal: 'Occupational Medicine',
      year: '2014',
      url: 'academic.oup.com/occmed/article/65/1/86/1433061',
      citation: 'Craig Jackson. "Chalder Fatigue Scale." Oxford Academic, Occupational Medicine, 29 Dec. 2014, academic.oup.com/occmed/article/65/1/86/1433061?login=true.'
    },
    {
      id: 14,
      category: 'Depression',
      title: 'PHQ-9: Depression Severity Measure',
      authors: 'Kroenke K, Spitzer RL, Williams JB',
      journal: 'Journal of General Internal Medicine',
      year: '2001',
      citation: 'Kroenke K, Spitzer RL, Williams JB (2001 Sep). The PHQ-9: validity of a brief depression severity measure. J Gen Intern Med. 16(9):606-13.'
    },
    {
      id: 15,
      category: 'Self-Esteem',
      title: 'Rosenberg Self-Esteem Scale (RSS)',
      authors: 'Rosenberg, Morris',
      journal: 'Acceptance and Commitment Therapy',
      year: '1965',
      citation: 'Rosenberg, Morris. "Rosenberg self-esteem scale (RSE)." Acceptance and commitment therapy. Measures package 61.52 (1965): 18.'
    }
  ];

  const allReferences = [...references, ...additionalReferences];

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(allReferences.map(ref => ref.category)));
    const cats = ['All', ...uniqueCategories];
    return cats.sort();
  }, [allReferences]);

  const filteredReferences = useMemo(() => {
    return allReferences.filter(ref => {
      const matchesSearch = ref.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ref.authors.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ref.journal.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || ref.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory, allReferences]);

  const getYearColor = (year: string) => {
    const yearNum = parseInt(year);
    if (yearNum >= 2015) return 'text-green-600';
    if (yearNum >= 2005) return 'text-blue-600';
    if (yearNum >= 1995) return 'text-orange-600';
    return 'text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <BookOpen className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
              References & Citations
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
            Evidence-based foundation for HealthCity and Twin Brain services. We acknowledge the valuable 
            work of researchers and practitioners whose assessments and methodologies inform our practice.
          </p>
          
          {/* Copyright Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
              <div className="text-left">
                <p className="text-blue-800 font-medium mb-2">Copyright Notice</p>
                <p className="text-blue-700 text-sm">
                  Where we have drawn on materials from the work of other people, we have tried our best to reference 
                  the relevant sources. We ask that you inform us at{' '}
                  <a href="mailto:info@healthcity.org.uk" className="font-medium underline">
                    info@healthcity.org.uk
                  </a>{' '}
                  to help us correct any breach of copyright or if you think we omitted to acknowledge a given work 
                  and its author. You can help by emailing us any reference source you believe is relevant to any 
                  contents of this Twin Brain resource. Thank you in advance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, author, or journal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Book className="h-4 w-4" />
              <span>{filteredReferences.length} of {allReferences.length} references</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{categories.length - 1} categories</span>
            </div>
          </div>
        </div>

        {/* References List */}
        <div className="space-y-6">
          {filteredReferences.map((reference) => (
            <div key={reference.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        {reference.category}
                      </span>
                      <span className={`text-sm font-medium ${getYearColor(reference.year)}`}>
                        {reference.year}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {reference.title}
                    </h3>
                    <p className="text-gray-700 mb-1">
                      <strong>Authors:</strong> {reference.authors}
                    </p>
                    <p className="text-gray-700 mb-3">
                      <strong>Journal:</strong> {reference.journal}
                    </p>
                  </div>
                  {reference.url && (
                    <a
                      href={`https://${reference.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Visit external link"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  )}
                </div>
                
                {/* Full Citation */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>Citation:</strong> {reference.citation}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredReferences.length === 0 && (
          <div className="text-center py-12">
            <Book className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No references found</h3>
            <p className="text-gray-600">
              Try adjusting your search terms or selecting a different category.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center text-gray-600">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Contributing to Our Reference Database</h3>
            <p className="text-gray-700 max-w-3xl mx-auto mb-6">
              We continuously update our reference database to ensure our services are based on the latest 
              evidence-based practices. If you have suggestions for additional references or notice any 
              errors, please contact us.
            </p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="mailto:info@healthcity.org.uk?subject=Reference Suggestion"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Suggest a Reference
              </a>
              <a
                href="mailto:info@healthcity.org.uk?subject=Reference Correction"
                className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Report an Issue
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferencePage;